/**
 * Fits the map viewport to the site plan's geographic bounding box.
 *
 * Single responsibility: one-shot fitBounds when both the site map resource
 * and the Maps SDK instance are available.
 *
 * @returns {import('./app').Plugin}
 */

import { take } from 'rxjs/operators';
import siteMapResource$ from '../redux/observables';

export default function createSiteMapViewportPlugin() {
  const state = {
    started:              false,
    mapFacade:            null,
    pendingResource:      null,
    resourceSubscription: null,
    mapReadySubscription: null,
    mapReady:             false,
    fitted:               false,
  };

  return {
    name: 'site-map-viewport',

    async start(context) {
      if (state.started) {
        return;
      }

      const mapFacade = context.services.mapFacade;
      if (!mapFacade) {
        return;
      }

      state.mapFacade = mapFacade;
      state.started = true;

      function tryFitBounds() {
        if (state.fitted || !state.pendingResource || !state.mapReady) {
          return;
        }

        const { bottomLeftLatitude, bottomLeftLongitude, topRightLatitude, topRightLongitude } = state.pendingResource;

        if (!Number.isFinite(bottomLeftLatitude)  ||
            !Number.isFinite(bottomLeftLongitude) ||
            !Number.isFinite(topRightLatitude)    ||
            !Number.isFinite(topRightLongitude)) {
          return;
        }

        state.fitted = true;

        mapFacade.fitBounds({
          minLatitude:  bottomLeftLatitude,
          minLongitude: bottomLeftLongitude,
          maxLatitude:  topRightLatitude,
          maxLongitude: topRightLongitude,
        });
      }

      state.resourceSubscription = siteMapResource$.subscribe((resource) => {
        state.pendingResource = resource;
        tryFitBounds();
      });

      state.mapReadySubscription = context.services.mapLibraryLoaded$
        .pipe(take(1))
        .subscribe(() => {
          state.mapReady = true;
          tryFitBounds();
        });
    },

    async stop() {
      state.resourceSubscription?.unsubscribe?.();
      state.resourceSubscription = null;

      state.mapReadySubscription?.unsubscribe?.();
      state.mapReadySubscription = null;

      state.mapFacade       = null;
      state.pendingResource = null;
      state.mapReady        = false;
      state.fitted          = false;
      state.started         = false;
    },
  };
}
